import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Card, Modal, ModalBody } from "reactstrap";
import {
  Table,
  Empty,
  Tag,
  Select,
  Descriptions,
  Statistic,
  Card as AntCard,
  Divider,
  Tooltip,
  Input,
  Drawer,
  Badge as AntBadge,
} from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import {
  EyeOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
  AppstoreOutlined,
  GiftOutlined,
  MailOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  TagOutlined,
} from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const invoiceStatusOptions = [
  { label: "Draft", value: "Draft" },
  { label: "Generated", value: "Generated" },
  { label: "Sent", value: "Sent" },
  { label: "Paid", value: "Paid" },
  { label: "Cancelled", value: "Cancelled" },
  { label: "Failed", value: "Failed" },
];

const paymentStatusOptions = [
  { label: "Unpaid", value: "Unpaid" },
  { label: "Partially Paid", value: "Partially Paid" },
  { label: "Fully Paid", value: "Fully Paid" },
  { label: "Overdue", value: "Overdue" },
];

const sourceTypeOptions = [
  { label: "Auto", value: "Auto" },
  { label: "Manual", value: "Manual" },
];

const invoiceStatusColor: Record<string, string> = {
  Draft: "default",
  Generated: "processing",
  Sent: "blue",
  Paid: "success",
  "Partially Paid": "warning",
  Cancelled: "error",
  Failed: "error",
};

const paymentStatusColor: Record<string, string> = {
  Unpaid: "orange",
  "Partially Paid": "gold",
  "Fully Paid": "green",
  Overdue: "red",
  Cancelled: "default",
};

// --- PDF Generation ---
const loadLogoAsBase64 = (): Promise<string> => {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve("");
      img.src = require("../../../images/logo-dark2x.png");
    } catch {
      resolve("");
    }
  });
};

const numberToWordsINR = (num: number): string => {
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convert = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
    if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
    return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
  };
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let result = "Indian Rupee " + convert(rupees);
  if (paise > 0) result += " and " + convert(paise) + " Paise";
  return result + " Only";
};

const generateInvoicePdf = async (inv: any, formatCurrency: (v: any, code?: string) => string) => {
  const doc = new jsPDF();
  const fmtAmt = (v: any) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  const logoBase64 = await loadLogoAsBase64();

  // ========== PAGE BORDER ==========
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

  // ========== HEADER: Logo + Company Info (left) | TAX INVOICE (right) ==========
  let y = 14;
  const logoW = 38;
  const logoH = 14;
  if (logoBase64) {
    try { doc.addImage(logoBase64, "PNG", margin, y - 4, logoW, logoH); } catch {}
  }

  const companyX = margin + logoW + 6;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("Procurex Technologies Private", companyX, y);
  doc.text("Limited", companyX, y + 5);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(50, 50, 50);
  const companyLines = [
    "H.No 4-49/2, Vattinagulapally",
    "Sy.No 237",
    "Gandipet, Hyderabad Telangana 500019",
    "India",
    "GSTIN 36AAOCP7271P1ZG",
    "8919901380",
    "bharath.devaram@procurextech.com",
    "www.procurextech.com",
  ];
  let companyY = y + 10;
  companyLines.forEach((line) => {
    doc.text(line, companyX, companyY);
    companyY += 3.5;
  });

  // TAX INVOICE on right
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("TAX INVOICE", pageWidth - margin, y + 6, { align: "right" });

  y = companyY + 4;

  // ========== INVOICE INFO SECTION ==========
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(margin, y, contentWidth, 22);

  const midX = margin + contentWidth * 0.55;
  doc.line(midX, y, midX, y + 22);

  // Left side: Invoice details
  const infoLabelX = margin + 3;
  const infoValX = margin + 35;
  let iy = y + 5;
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);

  doc.setFont("helvetica", "normal");
  doc.text("#", infoLabelX, iy);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.invoiceNumber || "NA"}`, infoValX, iy);

  iy += 4.5;
  doc.setFont("helvetica", "normal");
  doc.text("Invoice Date", infoLabelX, iy);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.invoiceDate ? moment(inv.invoiceDate).format("DD/MM/YYYY") : "-"}`, infoValX, iy);

  iy += 4.5;
  doc.setFont("helvetica", "normal");
  doc.text("Terms", infoLabelX, iy);
  doc.text(`: ${inv.billingPeriod || "Due on Receipt"}`, infoValX, iy);

  iy += 4.5;
  doc.setFont("helvetica", "normal");
  doc.text("Due Date", infoLabelX, iy);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.dueDate ? moment(inv.dueDate).format("DD/MM/YYYY") : "-"}`, infoValX, iy);

  // Right side: Place of Supply / Status
  const rightLabelX = midX + 3;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Place Of Supply", rightLabelX, y + 5);
  doc.setFont("helvetica", "bold");
  doc.text(": Telangana (36)", rightLabelX + 30, y + 5);

  doc.setFont("helvetica", "normal");
  doc.text("Status", rightLabelX, y + 10);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.status || "Draft"}`, rightLabelX + 30, y + 10);

  doc.setFont("helvetica", "normal");
  doc.text("Payment", rightLabelX, y + 15);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${inv.paymentStatus || "Unpaid"}`, rightLabelX + 30, y + 15);

  y += 22;

  // ========== BILL TO SECTION ==========
  // Bill To header bar
  doc.setFillColor(230, 230, 230);
  doc.rect(margin, y, contentWidth, 6, "F");
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, y, contentWidth, 6, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text("Bill To", margin + 3, y + 4.2);

  y += 6;
  // Bill To content box - dynamic height based on available info
  const billToLines: string[] = [];
  if (inv.customerCode) billToLines.push(`Customer Code: ${inv.customerCode}`);
  if (inv.customerGstin) billToLines.push(`GSTIN: ${inv.customerGstin}`);
  if (inv.customerPan) billToLines.push(`PAN: ${inv.customerPan}`);
  const cityState = [inv.customerCity, inv.customerState].filter(Boolean).join(", ");
  if (cityState) billToLines.push(cityState + (inv.customerPincode ? ` - ${inv.customerPincode}` : ""));
  else if (inv.customerPincode) billToLines.push(`Pincode: ${inv.customerPincode}`);
  if (inv.customerPhone) billToLines.push(`Phone: ${inv.customerPhone}`);
  if (inv.customerEmail) billToLines.push(inv.customerEmail);
  billToLines.push(`Currency: ${inv.currencyCode || "INR"}`);

  const billToBoxH = 8 + billToLines.length * 4;
  doc.rect(margin, y, contentWidth, billToBoxH, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(inv.customerName || "-", margin + 3, y + 5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  let billY = y + 9.5;
  billToLines.forEach((line) => {
    doc.text(line, margin + 3, billY);
    billY += 4;
  });

  y += billToBoxH;

  // ========== LINE ITEMS TABLE ==========
  const lines = inv.invoiceLines || [];
  const allLines = lines.map((l: any, i: number) => {
    const name = l.bundleId
      ? (l.bundleName || l.description || "-")
      : (l.appName || l.description || "-");
    const type = l.bundleId ? "[Enterprise]" : "[SaaS]";
    return [
      (i + 1).toString(),
      `${name} ${type}`,
      (l.quantity || 1).toString(),
      fmtAmt(l.unitPrice),
      fmtAmt(l.lineAmount || l.totalAmount),
    ];
  });

  y += 2;

  autoTable(doc, {
    startY: y,
    head: [["#", "Item & Description", "Qty", "Rate", "Amount"]],
    body: allLines,
    theme: "grid",
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    bodyStyles: {
      fontSize: 7.5,
      cellPadding: 2.5,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
    },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: 80 },
      2: { cellWidth: 18, halign: "center" },
      3: { halign: "right" },
      4: { halign: "right", fontStyle: "bold" },
    },
    margin: { left: margin, right: margin },
  });

  y = (doc as any).lastAutoTable.finalY;

  // ========== SUMMARY SECTION (two columns below table) ==========
  const summaryStartY = y + 2;
  const summaryRightW = 80;
  const summaryLeftW = contentWidth - summaryRightW;
  const summaryRightX = margin + summaryLeftW;

  // --- Right side: Financial Summary ---
  const summaryRows: [string, string][] = [];
  summaryRows.push(["Sub Total", fmtAmt(inv.subtotalAmount)]);
  if (inv.couponAmount > 0) {
    const couponLabel = inv.couponCode ? `Coupon (${inv.couponCode.length > 18 ? inv.couponCode.substring(0, 18) + "..." : inv.couponCode})` : "Coupon Discount";
    summaryRows.push([couponLabel, "- " + fmtAmt(inv.couponAmount)]);
  }
  if (inv.manualDiscountAmount > 0) summaryRows.push([`Manual Discount`, "- " + fmtAmt(inv.manualDiscountAmount)]);
  if (inv.totalDiscountAmount > 0) summaryRows.push(["Total Discount", "- " + fmtAmt(inv.totalDiscountAmount)]);
  if (inv.cgstAmount > 0) summaryRows.push([`CGST (${inv.cgstRate || 9}%)`, fmtAmt(inv.cgstAmount)]);
  if (inv.sgstAmount > 0) summaryRows.push([`SGST (${inv.sgstRate || 9}%)`, fmtAmt(inv.sgstAmount)]);
  if (inv.igstAmount > 0) summaryRows.push([`IGST (${inv.igstRate || 18}%)`, fmtAmt(inv.igstAmount)]);
  if (inv.taxAmount > 0 && !inv.cgstAmount && !inv.sgstAmount && !inv.igstAmount) summaryRows.push([`Tax (${inv.taxName || ""})`, fmtAmt(inv.taxAmount)]);
  if (inv.advanceAdjusted > 0) summaryRows.push(["Advance Adjusted", "- " + fmtAmt(inv.advanceAdjusted)]);

  // Draw summary rows on right
  let sy = summaryStartY;
  const labelColW = 52;
  const valColW = summaryRightW - labelColW;
  doc.setFontSize(7.5);

  summaryRows.forEach((row) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(summaryRightX, sy, labelColW, 5.5, "S");
    doc.rect(summaryRightX + labelColW, sy, valColW, 5.5, "S");
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(row[0], summaryRightX + labelColW - 2, sy + 4, { align: "right" });
    doc.text(row[1], summaryRightX + summaryRightW - 2, sy + 4, { align: "right" });
    sy += 5.5;
  });

  // Total row (bold, highlighted)
  doc.setFillColor(240, 240, 240);
  doc.rect(summaryRightX, sy, labelColW, 6, "FD");
  doc.rect(summaryRightX + labelColW, sy, valColW, 6, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Total", summaryRightX + labelColW - 2, sy + 4.5, { align: "right" });
  doc.text(`Rs.${fmtAmt(inv.totalAmount)}`, summaryRightX + summaryRightW - 2, sy + 4.5, { align: "right" });
  sy += 6;

  // Balance Due row (bold)
  doc.setFillColor(240, 240, 240);
  doc.rect(summaryRightX, sy, labelColW, 6, "FD");
  doc.rect(summaryRightX + labelColW, sy, valColW, 6, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Balance Due", summaryRightX + labelColW - 2, sy + 4.5, { align: "right" });
  doc.text(`Rs.${fmtAmt(inv.balanceAmount || inv.netPayableAmount)}`, summaryRightX + summaryRightW - 2, sy + 4.5, { align: "right" });
  sy += 6;

  // --- Left side: Total in Words ---
  let ly = summaryStartY + 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(0, 0, 0);
  doc.text("Total In Words", margin, ly);
  ly += 4;
  doc.setFont("helvetica", "bolditalic");
  doc.setFontSize(7);
  const totalWords = numberToWordsINR(Number(inv.totalAmount || 0));
  const wordLines = doc.splitTextToSize(totalWords, summaryLeftW - 8);
  doc.text(wordLines, margin, ly);
  ly += wordLines.length * 3.5 + 4;

  // Notes
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text("Notes", margin, ly);
  ly += 3.5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(inv.notes || "Thanks for your business.", margin, ly);
  ly += 6;

  // Bank Details
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text("Payment To Be Made to the Below Bank Account Details:", margin, ly);
  ly += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  const bankLines = [
    "Account Name: Procurex Technologies Private Limited",
    "Bank Name: Indusind Bank",
    "Account Number: 255557895777",
    "IFSC Code: INDB0001801",
  ];
  bankLines.forEach((line) => {
    doc.text(line, margin, ly);
    ly += 3.5;
  });

  // Authorized Signature on right
  const authSigY = sy + 8;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(summaryRightX + 5, authSigY, summaryRightX + summaryRightW - 2, authSigY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("Authorized Signature", summaryRightX + summaryRightW / 2, authSigY + 4, { align: "center" });

  // ========== TERMS & CONDITIONS ==========
  const termsY = Math.max(ly + 6, authSigY + 12);

  // Check if terms fit on current page, if not add new page
  if (termsY + 60 > pageHeight - 15) {
    doc.addPage();
    ly = 14;
  } else {
    ly = termsY;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text("Terms & Conditions", margin, ly);
  ly += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  const terms = [
    '1.  The payment against this Tax Invoice shall be made in favour of "Procurex Technologies Private Limited" and shall be vide RTGS, NEFT, NACH, IMPS, Account Payee Cheque, DD or credit/debit card.',
    "2.  Delayed interest rate of 18% per annum will be applicable for any delays in payment beyond the terms of payment as mentioned above.",
    "3.  Any request for return and replacement of delivered products or any request for return and refund shall be handled only in accordance with the Return, Replacement and Refund process outlined in the above referenced Sales Order.",
    "4.  Input tax reversal on the credit note issued by Procurex Technologies Private Limited towards any discount including any rate difference shall be done by you in the same month when such credit notes are issued and shall be disclosed in your periodical GST return.",
    "5.  As per the provisions of the Income Tax Act, you shall deposit the deducted TDS with the government authorities within the prescribed time limit without fail. If any loss is incurred by Procurex Technologies Limited due to your non-compliance, you shall be liable to compensate for such loss.",
    "6.  In case of ex-works, Procurex Technologies Private Limited's responsibility ceases once the product(s) is/are available for delivery to you and Procurex Technologies Private Limited will not be responsible for any shortage, loss or damage in transit.",
  ];

  terms.forEach((term) => {
    const termLines = doc.splitTextToSize(term, contentWidth - 4);
    doc.text(termLines, margin, ly);
    ly += termLines.length * 3 + 2;
  });

  // ========== PAGE BORDER & PAGE NUMBER on all pages ==========
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    doc.text(String(i), pageWidth - margin, pageHeight - 12, { align: "right" });
  }

  doc.save(`Invoice-${inv.invoiceNumber || inv.invoiceId}.pdf`);
};

const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState<string | undefined>(undefined);
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string | undefined>(undefined);
  const [filterSourceType, setFilterSourceType] = useState<string | undefined>(undefined);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Modals
  const [viewModal, setViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Bulk selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterInvoiceStatus) filters.status = filterInvoiceStatus;
    if (filterPaymentStatus) filters.paymentStatus = filterPaymentStatus;
    if (filterSourceType) filters.sourceType = filterSourceType;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterInvoiceStatus, filterPaymentStatus, filterSourceType]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const filters = params.filters !== undefined ? params.filters : buildFilters();
      const apiParams: any = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff ?? pagination.pageDiff,
        paginationId: params.paginationId ?? pagination.paginationId,
        searchQuery: params.searchQuery !== undefined ? params.searchQuery : onSearchText,
      };

      if (filters) {
        if (filters.status) apiParams.status = filters.status;
        if (filters.paymentStatus) apiParams["filters[paymentStatus]"] = filters.paymentStatus;
        if (filters.sourceType) apiParams["filters[sourceType]"] = filters.sourceType;
      }

      const { data, status, message } = await superAdminService.getInvoiceList(apiParams);

      if (status) {
        const list = Array.isArray(data) ? data : data?.list || [];
        const count = Array.isArray(data) ? data.length : data?.count || 0;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && { paginationId: params.paginationId }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P", searchQuery: debouncedSearch.trim() });
  }, [debouncedSearch]);

  const tableOnChange = async (newPagination: any) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };
    if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(((pagination.current - 1) * pagination.pageSize) / newPagination.pageSize);
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction = newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff = Math.abs(newPagination.current - pagination.current) - 1;
      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[dataSource.length - 1]?.paginationId || dataSource[dataSource.length - 1]?.invoiceId;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.paginationId || dataSource[0]?.invoiceId;
      }
    }
    await fetchData(apiParams);
  };

  const handleClearFilters = () => {
    setFilterInvoiceStatus(undefined);
    setFilterPaymentStatus(undefined);
    setFilterSourceType(undefined);
  };

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
  }, [filterInvoiceStatus, filterPaymentStatus, filterSourceType]);

  const refreshList = () => {
    hasFetched.current = false;
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
  };

  // --- View Invoice ---
  const openViewModal = async (record: any) => {
    setViewModal(true);
    setDetailLoading(true);
    try {
      const res = await superAdminService.getInvoiceById(record.invoiceId);
      if (res.status && res.data) {
        setSelectedInvoice(res.data);
      } else {
        Swal.fire("Error", res.message || "Invoice not found", "error");
        setViewModal(false);
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
      setViewModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // --- Actions ---
  const handleSend = async (invoiceId: number) => {
    try {
      const res = await superAdminService.sendInvoice({ invoiceId });
      if (res.status) {
        Swal.fire("Success", "Invoice sent successfully", "success");
        setViewModal(false);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const handleResend = async (invoiceId: number) => {
    try {
      const res = await superAdminService.resendInvoice({ invoiceId });
      if (res.status) {
        Swal.fire("Success", "Invoice resent successfully", "success");
        setViewModal(false);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const handleCancel = async (invoiceId: number) => {
    const result = await Swal.fire({
      title: "Cancel Invoice?",
      text: "Are you sure you want to cancel this invoice?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      confirmButtonColor: "#d33",
      input: "text",
      inputLabel: "Reason (optional)",
      inputPlaceholder: "Enter cancellation reason",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.cancelInvoice({ invoiceId, reason: result.value || undefined });
      if (res.status) {
        Swal.fire("Cancelled", "Invoice cancelled successfully", "success");
        setViewModal(false);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const handleRetry = async (invoiceId: number) => {
    try {
      const res = await superAdminService.retryInvoice(invoiceId);
      if (res.status) {
        Swal.fire("Success", "Invoice retry initiated", "success");
        setViewModal(false);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  // --- Bulk Actions ---
  const handleBulkSend = async () => {
    if (selectedRowKeys.length === 0) return;
    const result = await Swal.fire({
      title: `Send ${selectedRowKeys.length} invoice(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, send all",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.bulkSendInvoices({ invoiceIds: selectedRowKeys });
      if (res.status) {
        Swal.fire("Success", "Invoices sent successfully", "success");
        setSelectedRowKeys([]);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const handleBulkRetry = async () => {
    if (selectedRowKeys.length === 0) return;
    const result = await Swal.fire({
      title: `Retry ${selectedRowKeys.length} invoice(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, retry all",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.bulkRetryInvoices({ invoiceIds: selectedRowKeys });
      if (res.status) {
        Swal.fire("Success", "Invoices retry initiated", "success");
        setSelectedRowKeys([]);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  // --- Format currency ---
  const formatCurrency = (amount: any, code?: string) => {
    if (amount === null || amount === undefined) return "-";
    const symbol = code === "USD" ? "$" : code === "EUR" ? "\u20AC" : "\u20B9";
    return `${symbol}${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // --- Download PDF for a record from list (fetch detail first) ---
  const handleDownloadPdf = async (record: any) => {
    try {
      const res = await superAdminService.getInvoiceById(record.invoiceId);
      if (res.status && res.data) {
        generateInvoicePdf(res.data, formatCurrency);
      } else {
        Swal.fire("Error", "Could not load invoice details for PDF", "error");
      }
    } catch {
      Swal.fire("Error", "Failed to generate PDF", "error");
    }
  };

  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 140,
      render: (text: string, record: any) => (
        <a
          href="#view"
          className="text-primary fw-bold"
          onClick={(e) => { e.preventDefault(); openViewModal(record); }}
        >
          {text || "NA"}
        </a>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: 150,
      ellipsis: true,
      render: (text: string, record: any) => (
        <Tooltip title={record.customerCode ? `${text} (${record.customerCode})` : text}>
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Period",
      dataIndex: "billingPeriod",
      key: "billingPeriod",
      width: 100,
      render: (text: string, record: any) => {
        if (text) return text;
        if (record.billingMonth) return moment(record.billingMonth).format("MMM-YY");
        return "-";
      },
    },
    {
      title: "Type",
      dataIndex: "sourceType",
      key: "sourceType",
      width: 80,
      render: (type: string) => <Tag color={type === "Auto" ? "cyan" : "purple"}>{type || "-"}</Tag>,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 105,
      render: (date: string) => date ? moment(date).format("DD MMM YY") : "-",
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 105,
      render: (date: string, record: any) => {
        if (!date) return "-";
        const isPaid = parseFloat(record.balanceAmount || 0) <= 0;
        const isOverdue = !isPaid && moment(date).isBefore(moment(), "day");
        return (
          <span style={{ color: isOverdue ? "#ff4d4f" : undefined, fontWeight: isOverdue ? 600 : undefined }}>
            {isOverdue && <ClockCircleOutlined style={{ marginRight: 3, fontSize: 11 }} />}
            {moment(date).format("DD MMM YY")}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 110,
      render: (val: any, record: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(val, record.currencyCode)}</span>,
    },
    {
      title: "Advance",
      dataIndex: "advanceAdjusted",
      key: "advanceAdjusted",
      width: 100,
      render: (val: any, record: any) => {
        const v = Number(val || 0);
        return v > 0 ? <span style={{ color: "#722ed1", fontWeight: 500 }}>{formatCurrency(v, record.currencyCode)}</span> : <span style={{ color: "#bbb" }}>-</span>;
      },
    },
    {
      title: "Paid",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 100,
      render: (val: any, record: any) => <span style={{ color: "#52c41a" }}>{formatCurrency(val || 0, record.currencyCode)}</span>,
    },
    {
      title: "Balance",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 110,
      render: (val: any, record: any) => {
        const balance = Math.max(Number(val || 0), 0);
        return <span style={{ fontWeight: 600, color: balance > 0 ? "#fa8c16" : "#52c41a" }}>{formatCurrency(balance, record.currencyCode)}</span>;
      },
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 110,
      render: (status: string) => <Tag color={paymentStatusColor[status] || "default"}>{status || "-"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status: string) => <Tag color={invoiceStatusColor[status] || "default"}>{status}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: "right" as const,
      render: (_: any, record: any) => (
        <div className="d-flex" style={{ gap: 3 }}>
          <Tooltip title="View">
            <a href="#view" className="btn btn-sm btn-icon btn-trigger"
              onClick={(e) => { e.preventDefault(); openViewModal(record); }}>
              <Icon name="eye" />
            </a>
          </Tooltip>
          <Tooltip title="Download PDF">
            <a href="#pdf" className="btn btn-sm btn-icon btn-trigger text-secondary"
              onClick={(e) => { e.preventDefault(); handleDownloadPdf(record); }}>
              <FilePdfOutlined style={{ fontSize: 15 }} />
            </a>
          </Tooltip>
          {(record.status === "Draft" || record.status === "Generated") && (
            <Tooltip title="Send (Coming Soon)">
              <a href="#send" className="btn btn-sm btn-icon btn-trigger text-muted"
                onClick={(e) => { e.preventDefault(); }}
                style={{ opacity: 0.45, cursor: "not-allowed", pointerEvents: "all" }}>
                <Icon name="send" />
              </a>
            </Tooltip>
          )}
          {record.status === "Sent" && (
            <Tooltip title="Resend (Coming Soon)">
              <a href="#resend" className="btn btn-sm btn-icon btn-trigger text-muted"
                onClick={(e) => { e.preventDefault(); }}
                style={{ opacity: 0.45, cursor: "not-allowed", pointerEvents: "all" }}>
                <Icon name="redo" />
              </a>
            </Tooltip>
          )}
          {record.status === "Failed" && (
            <Tooltip title="Retry (Coming Soon)">
              <a href="#retry" className="btn btn-sm btn-icon btn-trigger text-muted"
                onClick={(e) => { e.preventDefault(); }}
                style={{ opacity: 0.45, cursor: "not-allowed", pointerEvents: "all" }}>
                <Icon name="reload" />
              </a>
            </Tooltip>
          )}
          {(record.status === "Draft" || record.status === "Generated") && (
            <Tooltip title="Cancel">
              <a href="#cancel" className="btn btn-sm btn-icon btn-trigger text-danger"
                onClick={(e) => { e.preventDefault(); handleCancel(record.invoiceId); }}>
                <Icon name="cross-circle" />
              </a>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const hasActiveFilters = filterInvoiceStatus || filterPaymentStatus || filterSourceType;
  const activeFilterCount = [filterInvoiceStatus, filterPaymentStatus, filterSourceType].filter(Boolean).length;

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  // --- Render View Modal Content ---
  const renderInvoiceView = () => {
    if (!selectedInvoice) return null;
    const inv = selectedInvoice;
    const cc = inv.currencyCode || "INR";
    const fmt = (v: any) => formatCurrency(v, cc);

    const lines = inv.invoiceLines || [];
    const appLines = lines.filter((l: any) => l.appId && !l.bundleId);
    const bundleLines = lines.filter((l: any) => l.bundleId);

    return (
      <>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="title mb-1" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <EyeOutlined />
              Invoice: {inv.invoiceNumber || "NA"}
              <Tag color={invoiceStatusColor[inv.status] || "default"} style={{ marginLeft: 8 }}>{inv.status}</Tag>
              <Tag color={paymentStatusColor[inv.paymentStatus] || "default"}>{inv.paymentStatus}</Tag>
            </h5>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>
              {inv.sourceType && <Tag color={inv.sourceType === "Auto" ? "cyan" : "purple"} style={{ fontSize: 11 }}>{inv.sourceType}</Tag>}
              {inv.customerName} {inv.customerCode ? `(${inv.customerCode})` : ""}
              {" | "}{cc}
              {inv.sentAt && <span style={{ marginLeft: 8 }}><MailOutlined style={{ marginRight: 3 }} />Sent: {moment(inv.sentAt).format("DD MMM YYYY, hh:mm A")}</span>}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button color="secondary" size="sm" className="btn-dim" onClick={() => generateInvoicePdf(inv, formatCurrency)}>
              <FilePdfOutlined style={{ marginRight: 4 }} /> Download PDF
            </Button>
            {(inv.status === "Draft" || inv.status === "Generated") && (
              <Button color="secondary" size="sm" className="btn-dim" disabled title="Coming Soon">
                <Icon name="send" /> <span>Send</span>
              </Button>
            )}
            {inv.status === "Sent" && (
              <Button color="secondary" size="sm" className="btn-dim" disabled title="Coming Soon">
                <Icon name="redo" /> <span>Resend</span>
              </Button>
            )}
            {inv.status === "Failed" && (
              <Button color="secondary" size="sm" className="btn-dim" disabled title="Coming Soon">
                <Icon name="reload" /> <span>Retry</span>
              </Button>
            )}
            {(inv.status === "Draft" || inv.status === "Generated") && (
              <Button color="danger" size="sm" className="btn-dim" onClick={() => handleCancel(inv.invoiceId)}>
                <Icon name="cross-circle" /> <span>Cancel</span>
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row g-3 mb-3">
          <div className="col">
            <AntCard size="small" style={{ borderLeft: "3px solid #1890ff" }}>
              <Statistic title="Amount" value={Number(inv.totalAmount || 0).toFixed(2)}
                prefix={cc} valueStyle={{ color: "#1890ff", fontSize: 18 }} />
            </AntCard>
          </div>
          {Number(inv.advanceAdjusted || 0) > 0 && (
            <div className="col">
              <AntCard size="small" style={{ borderLeft: "3px solid #722ed1" }}>
                <Statistic title="Advance" value={Number(inv.advanceAdjusted || 0).toFixed(2)}
                  prefix={cc} valueStyle={{ color: "#722ed1", fontSize: 18 }} />
              </AntCard>
            </div>
          )}
          <div className="col">
            <AntCard size="small" style={{ borderLeft: "3px solid #52c41a" }}>
              <Statistic title="Paid" value={Number(inv.paidAmount || 0).toFixed(2)}
                prefix={cc} valueStyle={{ color: "#52c41a", fontSize: 18 }} />
            </AntCard>
          </div>
          <div className="col">
            <AntCard size="small" style={{ borderLeft: "3px solid #fa8c16" }}>
              <Statistic title="Balance" value={Math.max(Number(inv.balanceAmount || 0), 0).toFixed(2)}
                prefix={cc} valueStyle={{ color: "#fa8c16", fontSize: 18 }} />
            </AntCard>
          </div>
          <div className="col">
            <AntCard size="small" style={{ borderLeft: "3px solid #13c2c2" }}>
              <Statistic title="Net Payable" value={Number(inv.netPayableAmount || 0).toFixed(2)}
                prefix={cc} valueStyle={{ color: "#13c2c2", fontSize: 18 }} />
            </AntCard>
          </div>
        </div>

        {/* Invoice Info */}
        <AntCard title="Invoice Information" size="small" className="mb-3">
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
            <Descriptions.Item label="Invoice Number">{inv.invoiceNumber || "NA"}</Descriptions.Item>
            <Descriptions.Item label="Invoice Date">{inv.invoiceDate ? moment(inv.invoiceDate).format("DD MMM YYYY") : "-"}</Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {inv.dueDate ? (() => {
                const isPaid = parseFloat(inv.balanceAmount || 0) <= 0;
                const isOverdue = !isPaid && moment(inv.dueDate).isBefore(moment(), "day");
                return (
                  <span style={{ color: isOverdue ? "#ff4d4f" : undefined, fontWeight: isOverdue ? 600 : undefined }}>
                    {moment(inv.dueDate).format("DD MMM YYYY")}
                    {isOverdue && " (Overdue)"}
                  </span>
                );
              })() : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">{inv.customerName || "-"} {inv.customerCode ? <Tag style={{ marginLeft: 4 }}>{inv.customerCode}</Tag> : ""}</Descriptions.Item>
            <Descriptions.Item label="Billing Period">{inv.billingPeriod || "-"}</Descriptions.Item>
            <Descriptions.Item label="Currency"><Tag color="blue">{cc}</Tag></Descriptions.Item>
            {inv.emailStatus && inv.emailStatus !== "Not Sent" && (
              <Descriptions.Item label="Email Status">
                <Tag color={inv.emailStatus === "Sent" ? "green" : inv.emailStatus === "Failed" ? "red" : "default"}>
                  <MailOutlined style={{ marginRight: 4 }} />{inv.emailStatus}
                </Tag>
                {inv.sentTo && <span style={{ fontSize: 11, color: "#8c8c8c", marginLeft: 4 }}>{inv.sentTo}</span>}
              </Descriptions.Item>
            )}
            {inv.status === "Failed" && (
              <Descriptions.Item label="Failure Reason" span={2}>
                <Tag color="red" icon={<ExclamationCircleOutlined />}>{inv.failureReason || "Unknown"}</Tag>
                {inv.retryCount > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: "#8c8c8c" }}>Retried {inv.retryCount} time(s)</span>}
              </Descriptions.Item>
            )}
          </Descriptions>
        </AntCard>

        {/* Segregated Line Items */}
        {appLines.length > 0 && (
          <AntCard
            title={<span><AppstoreOutlined style={{ marginRight: 6, color: "#1890ff" }} />SaaS Pricing ({appLines.length})</span>}
            size="small"
            className="mb-3"
          >
            <Table
              dataSource={appLines}
              rowKey="invoiceLineId"
              pagination={false}
              size="small"
              bordered
              columns={[
                { title: "#", dataIndex: "lineNumber", key: "lineNumber", width: 45, render: (_: any, __: any, i: number) => i + 1 },
                { title: "Application", dataIndex: "appName", key: "appName", render: (v: string, r: any) => v || r.description || "-" },
                { title: "Description", dataIndex: "description", key: "description", render: (v: string) => v || "-" },
                { title: "Qty", dataIndex: "quantity", key: "quantity", width: 60, render: (v: any) => v || 1 },
                { title: "Unit Price", dataIndex: "unitPrice", key: "unitPrice", width: 110, render: (v: any) => fmt(v) },
                { title: "Amount", dataIndex: "lineAmount", key: "lineAmount", width: 110, render: (v: any, r: any) => <strong>{fmt(v || r.totalAmount)}</strong> },
              ]}
              summary={() => {
                const total = appLines.reduce((s: number, l: any) => s + Number(l.lineAmount || l.totalAmount || 0), 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right"><strong>SaaS Pricing Subtotal</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}><strong style={{ color: "#1890ff" }}>{fmt(total)}</strong></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </AntCard>
        )}

        {bundleLines.length > 0 && (
          <AntCard
            title={<span><GiftOutlined style={{ marginRight: 6, color: "#722ed1" }} />Enterprise Pricing ({bundleLines.length})</span>}
            size="small"
            className="mb-3"
          >
            <Table
              dataSource={bundleLines}
              rowKey="invoiceLineId"
              pagination={false}
              size="small"
              bordered
              columns={[
                { title: "#", dataIndex: "lineNumber", key: "lineNumber", width: 45, render: (_: any, __: any, i: number) => i + 1 },
                { title: "Bundle", dataIndex: "bundleName", key: "bundleName", render: (v: string, r: any) => v || r.description || "-" },
                { title: "Description", dataIndex: "description", key: "description", render: (v: string) => v || "-" },
                { title: "Qty", dataIndex: "quantity", key: "quantity", width: 60, render: (v: any) => v || 1 },
                { title: "Unit Price", dataIndex: "unitPrice", key: "unitPrice", width: 110, render: (v: any) => fmt(v) },
                { title: "Amount", dataIndex: "lineAmount", key: "lineAmount", width: 110, render: (v: any, r: any) => <strong>{fmt(v || r.totalAmount)}</strong> },
              ]}
              summary={() => {
                const total = bundleLines.reduce((s: number, l: any) => s + Number(l.lineAmount || l.totalAmount || 0), 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right"><strong>Enterprise Pricing Subtotal</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={5}><strong style={{ color: "#722ed1" }}>{fmt(total)}</strong></Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </AntCard>
        )}

        {/* Invoice Breakup */}
        <AntCard title="Invoice Breakup" size="small" className="mb-3">
          <div style={{ maxWidth: 450, marginLeft: "auto" }}>
            <Descriptions column={1} bordered size="small" labelStyle={{ width: 200 }}>
              <Descriptions.Item label="Subtotal">{fmt(inv.subtotalAmount)}</Descriptions.Item>
              {inv.couponAmount > 0 && (
                <Descriptions.Item label={<span>Coupon Discount {inv.couponCode && <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>{inv.couponCode}</Tag>}</span>}>
                  <span className="text-danger">-{fmt(inv.couponAmount)}</span>
                </Descriptions.Item>
              )}
              {inv.manualDiscountAmount > 0 && (
                <Descriptions.Item label={`Manual Discount (${inv.manualDiscountType || ""}${inv.manualDiscountType === "Percentage" ? ` ${inv.manualDiscountValue}%` : ""})`}>
                  <span style={{ color: "#fa8c16" }}>-{fmt(inv.manualDiscountAmount)}</span>
                </Descriptions.Item>
              )}
              {inv.totalDiscountAmount > 0 && (
                <Descriptions.Item label="Total Discount">
                  <span className="text-danger">-{fmt(inv.totalDiscountAmount)}</span>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={<strong>Taxable Amount</strong>}><strong>{fmt(inv.taxableAmount)}</strong></Descriptions.Item>
              {inv.taxAmount > 0 && (
                <Descriptions.Item label={`Tax (${inv.taxName || ""})`}>{fmt(inv.taxAmount)}</Descriptions.Item>
              )}
              {inv.advanceAdjusted > 0 && (
                <Descriptions.Item label={<span>Advance Adjusted {inv.advanceReferenceNo && <span style={{ fontSize: 10, color: "#8c8c8c" }}>({inv.advanceReferenceNo})</span>}</span>}>
                  <span className="text-danger">-{fmt(inv.advanceAdjusted)}</span>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={<strong style={{ fontSize: 14 }}>Total Amount</strong>}>
                <strong style={{ fontSize: 16, color: "#1890ff" }}>{fmt(inv.totalAmount)}</strong>
              </Descriptions.Item>
              {inv.paidAmount > 0 && (
                <Descriptions.Item label="Paid Amount">
                  <span style={{ color: "#52c41a", fontWeight: 600 }}>{fmt(inv.paidAmount)}</span>
                </Descriptions.Item>
              )}
              {inv.balanceAmount > 0 && (
                <Descriptions.Item label={<strong>Balance Due</strong>}>
                  <strong style={{ color: "#fa8c16" }}>{fmt(inv.balanceAmount)}</strong>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        </AntCard>

        {/* Advance Adjustments */}
        {inv.advanceAdjustments && inv.advanceAdjustments.length > 0 && (
          <AntCard title="Advance Adjustments" size="small" className="mb-3">
            <Table
              dataSource={inv.advanceAdjustments}
              rowKey="adjustmentId"
              pagination={false}
              size="small"
              bordered
              columns={[
                { title: "Advance Ref", dataIndex: "advanceReferenceNo", key: "advanceReferenceNo", render: (v: string) => v || "-" },
                { title: "Adjusted Amount", dataIndex: "adjustedAmount", key: "adjustedAmount", render: (v: any) => fmt(v) },
                { title: "Advance Date", dataIndex: "advanceDate", key: "advanceDate", render: (v: string) => v ? moment(v).format("DD MMM YYYY") : "-" },
                { title: "Adjustment Date", dataIndex: "adjustmentDate", key: "adjustmentDate", render: (v: string) => v ? moment(v).format("DD MMM YYYY") : "-" },
                { title: "Remarks", dataIndex: "remarks", key: "remarks", ellipsis: true, render: (v: string) => v || "-" },
              ]}
            />
          </AntCard>
        )}

        {/* Notes */}
        {inv.notes && (
          <AntCard size="small" className="mb-3" style={{ background: "#fffbe6", borderColor: "#ffe58f" }}>
            <strong>Notes:</strong> {inv.notes}
            {inv.discountReason && <><Divider type="vertical" /><strong>Discount Reason:</strong> {inv.discountReason}</>}
          </AntCard>
        )}

        <div className="mt-3 text-end">
          <Button color="secondary" size="sm" onClick={() => setViewModal(false)}>Close</Button>
        </div>
      </>
    );
  };

  return (
    <React.Fragment>
      <Head title="Invoice Management" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Invoice Management
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} invoices.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                {selectedRowKeys.length > 0 && (
                  <>
                    <li>
                      <Button color="secondary" className="btn-dim" disabled title="Coming Soon">
                        <Icon name="send"></Icon>
                        <span>Send ({selectedRowKeys.length})</span>
                      </Button>
                    </li>
                    <li>
                      <Button color="secondary" className="btn-dim" disabled title="Coming Soon">
                        <Icon name="reload"></Icon>
                        <span>Retry ({selectedRowKeys.length})</span>
                      </Button>
                    </li>
                  </>
                )}
                <li className="nk-block-tools-opt">
                  <Button color="primary" onClick={() => navigate("/sa-invoices/create")}>
                    <Icon name="plus"></Icon>
                    <span>Manual Invoice</span>
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner py-3">
              <div className="d-flex justify-content-between align-items-center">
                <Input
                  prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                  placeholder="Search by invoice number, customer..."
                  value={onSearchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="borderless"
                  style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                  allowClear
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Tooltip title="Refresh">
                    <span
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", transition: "background 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => {
                        hasFetched.current = false;
                        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                      }}
                    >
                      <ReloadOutlined style={{ fontSize: 16, color: "#555" }} />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <span
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", cursor: "pointer", transition: "background 0.2s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      onClick={() => setFilterDrawerOpen(true)}
                    >
                      <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                        <FilterOutlined style={{ fontSize: 16, color: "#555" }} />
                      </AntBadge>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="card-inner p-0">
              {loading ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                      {["Invoice No", "Customer", "Period", "Type", "Invoice Date", "Due Date", "Amount", "Advance", "Paid", "Balance", "Payment", "Status", "Actions"].map((h) => (
                        <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 90, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 110, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 45, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 60, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 60, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 65, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 55, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 18, borderRadius: 4 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : dataSource.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="invoiceId"
                  rowSelection={rowSelection}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                    showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
                  }}
                  onChange={tableOnChange}
                  scroll={{ 
                    x: 1400,
                    //  y: 500
                     }}
                  bordered
                  size="small"
                />
              ) : (
                <Empty description="No invoices found" />
              )}
            </div>
          </Card>
        </Block>

      </Content>

        {/* View Invoice Modal */}
        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="xl" style={{ maxWidth: "1000px" }}>
          <ModalBody style={{ padding: "20px 24px" }}>
            {detailLoading ? (
              <LoadingSpinner />
            ) : renderInvoiceView()}
          </ModalBody>
        </Modal>

        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} />
              <span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>
              {activeFilterCount > 0 && (
                <AntBadge count={activeFilterCount} style={{ backgroundColor: "#6576ff", marginLeft: 4 }} />
              )}
            </div>
          }
          placement="right"
          onClose={() => setFilterDrawerOpen(false)}
          open={filterDrawerOpen}
          width={450}
          zIndex={1060}
          extra={
            hasActiveFilters ? (
              <span style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }} onClick={handleClearFilters}>
                Reset All
              </span>
            ) : null
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)", border: "1px solid #d6e4ff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 15 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Invoice Status</span>
              </div>
              <Select placeholder="All statuses" allowClear style={{ width: "100%" }} value={filterInvoiceStatus} onChange={(val) => setFilterInvoiceStatus(val)} options={invoiceStatusOptions} />
            </div>
            <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #fff7e6 0%, #fff1f0 100%)", border: "1px solid #ffd591" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <CreditCardOutlined style={{ color: "#fa8c16", fontSize: 15 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Payment Status</span>
              </div>
              <Select placeholder="All payment statuses" allowClear style={{ width: "100%" }} value={filterPaymentStatus} onChange={(val) => setFilterPaymentStatus(val)} options={paymentStatusOptions} />
            </div>
            <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)", border: "1px solid #d9f7be" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <TagOutlined style={{ color: "#52c41a", fontSize: 15 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Source Type</span>
              </div>
              <Select placeholder="All types" allowClear style={{ width: "100%" }} value={filterSourceType} onChange={(val) => setFilterSourceType(val)} options={sourceTypeOptions} />
            </div>
          </div>
        </Drawer>
    </React.Fragment>
  );
};

export default InvoiceList;
